import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
  startOfYear,
  subMonths,
} from 'date-fns'

import * as UI from '@mantine/core'
import * as Dates from '@mantine/dates'

import { buildComponent } from '../../../../components/factory'
import * as Icons from '../../../../components/icons'
import { useStoreState } from '../../../../hooks/state'
import {
  DataExport,
  DataExportStatus,
  failureMessage,
  requestErrorMessage,
  serviceExports,
} from '../../../../services/exports'

// Onglet « Export » des statistiques (#7688), réservé à l'équipe Nap&Up : il
// n'est pas proposé aux comptes client, et l'API refuse leurs demandes. La
// génération se fait côté API, en tâche de fond : on demande l'export, on suit
// son état, puis on télécharge le ZIP.

type Range = [Date | null, Date | null]

const POLL_MS = 5000

const presets: { label: string; range: () => Range }[] = [
  {
    label: 'Mois en cours',
    range: () => [startOfMonth(new Date()), startOfDay(new Date())],
  },
  {
    label: 'Mois précédent',
    range: () => [
      startOfMonth(subMonths(new Date(), 1)),
      startOfDay(endOfMonth(subMonths(new Date(), 1))),
    ],
  },
  {
    label: 'Année en cours',
    range: () => [startOfYear(new Date()), startOfDay(new Date())],
  },
  {
    label: '12 derniers mois',
    range: () => [
      startOfMonth(subMonths(new Date(), 12)),
      startOfDay(endOfMonth(subMonths(new Date(), 1))),
    ],
  },
]

const FILES = [
  ['reservations.csv', 'une ligne par sieste, annulations comprises'],
  ['ecoutes.csv', 'une ligne par écoute d’un audio'],
  [
    'utilisateurs.csv',
    'date de création, dernière ouverture de l’app, espaces',
  ],
  ['adhesions.csv', 'arrivées et départs des utilisateurs dans chaque espace'],
  ['synthese_espaces.csv', 'les totaux de la période, une ligne par espace'],
  ['evaluations.csv, retours.csv, demandes_sans_creneau.csv', 'en complément'],
]

const STATUS: Record<DataExportStatus, { label: string; color: string }> = {
  pending: { label: 'En attente', color: 'gray' },
  running: { label: 'En cours', color: 'blue' },
  done: { label: 'Prêt', color: 'green' },
  failed: { label: 'Échec', color: 'red' },
  expired: { label: 'Expiré', color: 'gray' },
}

const day = (value: string) => {
  const [year, month, date] = value.split('-')

  return `${date}/${month}/${year}`
}

const size = (bytes: number | null) => {
  if (!bytes) {
    return ''
  }

  return bytes < 1e6
    ? `${Math.max(1, Math.round(bytes / 1e3))} ko`
    : `${(bytes / 1e6).toFixed(1).replace('.', ',')} Mo`
}

export const Export = buildComponent()
  .withLifecycle(() => {
    const space = useStoreState((store) => store.statistics.space)
    const spaces = useStoreState((store) => store.space.list)

    const [range, setRange] = useState<Range>(presets[1].range())
    const [history, setHistory] = useState<DataExport[] | null>(null)
    const [historyError, setHistoryError] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [message, setMessage] = useState<{
      color: 'green' | 'red'
      text: string
    } | null>(null)
    const [downloading, setDownloading] = useState<string | null>(null)

    // Historique de l'espace choisi, ou de tous les exports sans espace.
    // Seule la dernière requête compte : une réponse arrivée après un
    // changement d'espace est ignorée.
    const lastRequest = useRef(0)

    const refresh = useCallback(async () => {
      const request = ++lastRequest.current

      try {
        const items = await serviceExports.history(space)

        if (request === lastRequest.current) {
          setHistory(items)
          setHistoryError(false)
        }
      } catch (err) {
        if (request === lastRequest.current) {
          setHistoryError(true)
        }
      }
    }, [space])

    useEffect(() => {
      setHistory(null)
      setHistoryError(false)
      refresh()
    }, [refresh])

    // Rafraîchissement tant qu'un export est en attente ou en cours.
    const inProgress = !!history?.some(
      (item) => item.status === 'pending' || item.status === 'running',
    )

    useEffect(() => {
      if (!inProgress) {
        return
      }

      const timer = setInterval(refresh, POLL_MS)

      return () => clearInterval(timer)
    }, [inProgress, refresh])

    const scopeLabel = useMemo(() => {
      if (space) {
        return (
          spaces.find((item) => item._id === space)?.name ??
          'Espace sélectionné'
        )
      }

      return 'Tous les espaces'
    }, [space, spaces])

    const submit = async () => {
      const [start, end] = range

      if (!start || !end) {
        setMessage({
          color: 'red',
          text: 'Choisissez une date de début et une date de fin.',
        })
        return
      }

      setSubmitting(true)
      setMessage(null)

      try {
        const result = await serviceExports.request(start, end, space)

        if (result.error) {
          setMessage({ color: 'red', text: requestErrorMessage(result.error) })
        } else {
          setMessage({
            color: 'green',
            text: 'Export lancé. Vous recevrez un email dès qu’il sera prêt ; il apparaîtra aussi ci-dessous.',
          })
        }

        await refresh()
      } catch (err) {
        setMessage({
          color: 'red',
          text: "L'export n'a pas pu être lancé. Réessayez dans un instant.",
        })
      } finally {
        setSubmitting(false)
      }
    }

    const download = async (item: DataExport) => {
      setDownloading(item._id)
      setMessage(null)

      try {
        await serviceExports.download(item)
      } catch (err) {
        setMessage({
          color: 'red',
          text: 'Le téléchargement a échoué. Le fichier a peut-être expiré, ou vos accès ont changé.',
        })
        await refresh()
      } finally {
        setDownloading(null)
      }
    }

    return {
      range,
      setRange,
      space,
      scopeLabel,
      submit,
      submitting,
      message,
      history,
      historyError,
      download,
      downloading,
    }
  })
  .withRender(({ lifecycle }) => (
    <UI.Stack spacing="xl" p="md">
      <UI.Stack spacing={4}>
        <UI.Title order={3}>Exporter les données</UI.Title>
        <UI.Text color="dimmed" size="sm">
          Les fichiers CSV s’ouvrent directement dans Excel. Ils ne contiennent
          ni email ni nom : chaque utilisateur est désigné par son identifiant
          Nap&Up, le même dans tous les fichiers.
        </UI.Text>
      </UI.Stack>

      <UI.Stack spacing="sm">
        <UI.Group align="flex-end" spacing="sm">
          <Dates.DateRangePicker
            label="Période"
            placeholder="Du … au …"
            locale="fr"
            inputFormat="DD/MM/YYYY"
            labelSeparator="au"
            allowSingleDateInRange
            amountOfMonths={2}
            clearable={false}
            value={lifecycle.range}
            onChange={lifecycle.setRange}
            sx={{ width: 300 }}
          />
          {presets.map((preset) => (
            <UI.Button
              key={preset.label}
              variant="light"
              color="gray"
              size="xs"
              mb={4}
              onClick={() => lifecycle.setRange(preset.range())}
            >
              {preset.label}
            </UI.Button>
          ))}
        </UI.Group>

        <UI.Text size="sm">
          <UI.Text component="span" weight={600}>
            Espaces :
          </UI.Text>{' '}
          {lifecycle.scopeLabel}
          <UI.Text component="span" color="dimmed">
            {' '}
            (choisissez un espace dans la liste de gauche, ou aucun pour tous
            les espaces)
          </UI.Text>
        </UI.Text>

        <UI.Group>
          <UI.Button
            onClick={lifecycle.submit}
            loading={lifecycle.submitting}
            leftIcon={<Icons.FontAwesomeIcon icon={Icons.Solid.faFileExport} />}
          >
            Lancer l’export
          </UI.Button>
        </UI.Group>

        {lifecycle.message && (
          <UI.Alert color={lifecycle.message.color}>
            {lifecycle.message.text}
          </UI.Alert>
        )}
      </UI.Stack>

      <UI.Stack spacing={4}>
        <UI.Text weight={600} size="sm">
          Contenu du fichier ZIP
        </UI.Text>
        <UI.List size="sm" spacing={2}>
          {FILES.map(([file, description]) => (
            <UI.List.Item key={file}>
              <UI.Code>{file}</UI.Code> : {description}
            </UI.List.Item>
          ))}
          <UI.List.Item>
            <UI.Code>LISEZMOI.txt</UI.Code> : la définition de chaque colonne
          </UI.List.Item>
        </UI.List>
      </UI.Stack>

      <UI.Stack spacing="xs">
        <UI.Text weight={600} size="sm">
          Derniers exports
          {lifecycle.space ? ` : ${lifecycle.scopeLabel}` : ''}
        </UI.Text>
        {lifecycle.historyError && (
          <UI.Alert color="red">
            La liste des exports n’a pas pu être chargée.
          </UI.Alert>
        )}
        {lifecycle.history && lifecycle.history.length === 0 && (
          <UI.Text color="dimmed" size="sm">
            {lifecycle.space
              ? 'Aucun export pour cet espace.'
              : 'Aucun export pour le moment.'}
          </UI.Text>
        )}
        {lifecycle.history && lifecycle.history.length > 0 && (
          <UI.Table verticalSpacing="xs" fontSize="sm">
            <thead>
              <tr>
                <th>Demandé le</th>
                <th>Période</th>
                <th>Espaces</th>
                <th>État</th>
                <th>Fichier</th>
              </tr>
            </thead>
            <tbody>
              {lifecycle.history.map((item) => (
                <tr key={item._id}>
                  <td>
                    {format(new Date(item.createdAt), 'dd/MM/yyyy HH:mm')}
                  </td>
                  <td>
                    du {day(item.startDay)} au {day(item.endDay)}
                  </td>
                  <td>{item.label}</td>
                  <td>
                    <UI.Group spacing={6} noWrap>
                      <UI.Badge color={STATUS[item.status].color}>
                        {STATUS[item.status].label}
                      </UI.Badge>
                      {item.status === 'running' && <UI.Loader size="xs" />}
                    </UI.Group>
                    {item.status === 'failed' && (
                      <UI.Text size="xs" color="red">
                        {failureMessage(item.error)}
                      </UI.Text>
                    )}
                  </td>
                  <td>
                    {item.status === 'done' && (
                      <UI.Button
                        size="xs"
                        variant="light"
                        loading={lifecycle.downloading === item._id}
                        leftIcon={
                          <Icons.FontAwesomeIcon
                            icon={Icons.Solid.faDownload}
                          />
                        }
                        onClick={() => lifecycle.download(item)}
                      >
                        Télécharger {size(item.size)}
                      </UI.Button>
                    )}
                    {item.status === 'expired' && (
                      <UI.Text size="xs" color="dimmed">
                        Supprimé après 7 jours
                      </UI.Text>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </UI.Table>
        )}
        <UI.Text color="dimmed" size="xs">
          Les fichiers sont conservés 7 jours, puis supprimés. Limite : 10
          exports par 24 heures, et un seul à la fois.
        </UI.Text>
      </UI.Stack>
    </UI.Stack>
  ))
